import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Button,
  Chip,
  Paper,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useAuth } from '../hooks/useAuth';
import { getReportById } from '../services/reportService';
import ReportAnalysisContent from '../components/dashboard/ReportAnalysisContent';
import ReportMetadataPanel from '../components/dashboard/ReportMetadataPanel';
import AISummaryCard from '../components/dashboard/AISummaryCard';
import ShareReportDialog from '../components/dashboard/ShareReportDialog';
import ReportViewTabs from '../components/dashboard/ReportViewTabs';
import ReportTrendChart from '../components/dashboard/ReportTrendChart';
import ReportDetailActions from '../components/dashboard/ReportDetailActions';
import { ReportDetailSkeleton } from '../components/dashboard/PageSkeletons';
import DecisionRequiredBanner from '../components/dashboard/DecisionRequiredBanner';
import SimilarTestsCard from '../components/dashboard/SimilarTestsCard';
import showNotification from '../components/common/Notification';
import { copyElementAsImage, exportReportToCSV } from '../utils/dashboardUtils';
import {
  buildSelectedMetricData,
  convertReportToCalculatorFormat,
  getReportBrandName,
} from '../utils/reportAnalysisUtils';
import '../components/dashboard/ReportListing.css';
import { canWriteReports, isReadOnlyUser } from '../utils/roleUtils';

/**
 * Full-page report detail view with analysis, export, and calculator handoff
 */
const ReportDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const contentRef = useRef(null);

  const [report, setReport] = useState(null);
  const [metricIndex, setMetricIndex] = useState(0);
  const [viewTab, setViewTab] = useState('analysis');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  const readOnly = isReadOnlyUser(user);
  const canShare = canWriteReports(user) && (user?.role === 'admin' || user?.role === 'editor');

  const userBrandIds = useMemo(
    () => (user?.role === 'admin' ? [] : user?.brands?.map((b) => b._id) || []),
    [user],
  );

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getReportById(id, userBrandIds);
        setReport(data);
        setMetricIndex(0);
      } catch (err) {
        console.error('Error fetching report:', err);
        setError('Rapor detayları yüklenirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchReport();
  }, [id, userBrandIds]);

  const brandName = getReportBrandName(report);

  const handleMetricChange = useCallback((index) => {
    setMetricIndex(index);
  }, []);

  const handleExportCSV = useCallback(() => {
    if (!report) return;

    const selectedData = buildSelectedMetricData(report, metricIndex);
    exportReportToCSV(
      selectedData,
      () => showNotification('CSV dosyası indirildi', 'success'),
      () => showNotification('CSV dışa aktarırken bir hata oluştu', 'error'),
    );
  }, [report, metricIndex]);

  const handleCopyImage = useCallback(async () => {
    if (!contentRef.current) return;

    await copyElementAsImage(
      contentRef.current,
      () => showNotification('Rapor görüntüsü panoya kopyalandı', 'success'),
      () => showNotification('Ekran görüntüsü alınırken bir hata oluştu', 'error'),
    );
  }, []);

  const handleCalculate = useCallback(() => {
    if (!report) return;

    try {
      const selectedData = buildSelectedMetricData(report, metricIndex);
      const calculatorData = convertReportToCalculatorFormat({
        ...selectedData,
        _id: report._id,
        averageOrderValue: report.averageOrderValue,
      });
      localStorage.setItem('calculatorImportData', JSON.stringify(calculatorData));
      showNotification("Rapor verileri Calculator'a aktarıldı", 'success');
      navigate('/dashboard/calculator');
    } catch (err) {
      console.error("Calculator'a veri aktarımında hata:", err);
      showNotification('Veri aktarımında bir hata oluştu', 'error');
    }
  }, [report, metricIndex, navigate]);

  const handleAiSummaryUpdated = useCallback((aiSummary) => {
    setReport((prev) => (prev ? { ...prev, aiSummary } : prev));
  }, []);

  if (loading) {
    return <ReportDetailSkeleton />;
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography color="error" gutterBottom>
          {error}
        </Typography>
        <Button variant="contained" onClick={() => navigate('/dashboard')} sx={{ bgcolor: '#240B4B' }}>
          Dashboard'a Dön
        </Button>
      </Box>
    );
  }

  if (!report) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography gutterBottom>Rapor bulunamadı.</Typography>
        <Button variant="contained" onClick={() => navigate('/dashboard')} sx={{ bgcolor: '#240B4B' }}>
          Dashboard'a Dön
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 2 }}
        aria-label="breadcrumb"
      >
        <Link
          component={RouterLink}
          to={readOnly ? '/dashboard/stakeholder' : '/dashboard'}
          underline="hover"
          color="inherit"
        >
          {readOnly ? 'Portföy' : 'Dashboard'}
        </Link>
        <Link
          component={RouterLink}
          to={`/dashboard?brand=${encodeURIComponent(brandName)}`}
          underline="hover"
          color="inherit"
        >
          {brandName}
        </Link>
        <Typography color="text.primary" sx={{ fontWeight: 600 }}>
          {report.reportName}
        </Typography>
      </Breadcrumbs>

      <Paper
        id="ga4-abtest-results"
        className="report-detail-page"
        elevation={0}
        sx={{ borderRadius: 2.5, boxShadow: '0 0 15px rgba(0,0,0,0.08)', overflow: 'hidden' }}
      >
        <Box id="ga4-abtest-content" ref={contentRef} sx={{ p: 2.5 }}>
          <Box
            className="popup-header report-detail-header"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
              mb: 2.5,
              pb: 2,
              borderBottom: '1px solid #eee',
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#240B4B', mb: 0.5 }}>
                {report.reportName}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                  {brandName}
                </Typography>
                {report.status && (
                  <Chip
                    label={report.status}
                    size="small"
                    color={
                      report.status === 'Canlı'
                        ? 'success'
                        : report.status === 'Taslak'
                          ? 'warning'
                          : 'default'
                    }
                  />
                )}
              </Box>
            </Box>
            <ReportDetailActions
              onCalculate={readOnly ? undefined : handleCalculate}
              onExportCsv={handleExportCSV}
              onCopyImage={handleCopyImage}
              onShare={readOnly ? undefined : () => setShareOpen(true)}
              showShare={canShare}
            />
          </Box>

          <DecisionRequiredBanner report={report} />

          <ReportMetadataPanel
            reportId={report._id}
            metadata={report}
            onUpdated={readOnly ? undefined : setReport}
            compact={readOnly}
          />

          <SimilarTestsCard report={report} />

          <AISummaryCard
            reportId={report._id}
            report={report}
            aiSummary={report.aiSummary}
            metricIndex={metricIndex}
            onUpdated={handleAiSummaryUpdated}
            readOnly={readOnly}
          />

          <ReportViewTabs activeTab={viewTab} onChange={setViewTab} />

          {viewTab === 'analysis' ? (
            <ReportAnalysisContent
              originalData={report}
              metricIndex={metricIndex}
              onMetricChange={handleMetricChange}
              reportId={report._id}
              onReportUpdated={setReport}
            />
          ) : (
            <ReportTrendChart
              reportId={report._id}
              defaultMetric={report.analysis?.[metricIndex]?.name}
            />
          )}
        </Box>
      </Paper>

      <ShareReportDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        reportId={report._id}
        reportName={report.reportName}
      />
    </Box>
  );
};

export default ReportDetail;
